/**
 * Landing page - entry point with role selection.
 * Mobile-first, optimized for low bandwidth with minimal assets.
 */
import React from 'react';
import { Link } from 'react-router-dom';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-600 via-primary-700 to-primary-900 text-white">
      {/* Hero Section */}
      <div className="max-w-lg mx-auto px-6 pt-12 pb-8 text-center">
        <div className="w-20 h-20 bg-white/20 rounded-3xl mx-auto mb-6 flex items-center justify-center backdrop-blur-sm">
          <span className="text-4xl">🏥</span>
        </div>
        
        <h1 className="text-3xl font-bold mb-3">
          Chronic Diseases<br />Consultation System
        </h1>
        
        <p className="text-primary-100 text-base mb-2">
          Access quality healthcare from anywhere in Kenya
        </p>
        <p className="text-primary-200 text-sm mb-8">
          Book consultations, connect with doctors, and manage your health — all from your phone
        </p>

        {/* Features */}
        <div className="grid grid-cols-2 gap-3 mb-10">
          {[
            { icon: '📱', label: 'Video Consultations' },
            { icon: '💊', label: 'Digital Prescriptions' },
            { icon: '📅', label: 'Easy Scheduling' },
            { icon: '🚨', label: 'Emergency Access' },
          ].map((f, i) => (
            <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <span className="text-2xl mb-1 block">{f.icon}</span>
              <span className="text-xs text-primary-100">{f.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Role Selection Cards */}
      <div className="bg-white rounded-t-3xl px-6 py-8 min-h-[40vh]">
        <h2 className="text-gray-900 text-xl font-bold text-center mb-6">
          Choose Your Portal
        </h2>

        {/* Patient Portal */}
        <Link to="/patient/login" className="block mb-4">
          <div className="card border-2 border-primary-100 hover:border-primary-300 transition-all hover:shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-primary-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                <span className="text-3xl">🧑‍⚕️</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg">Patient Portal</h3>
                <p className="text-sm text-gray-500">Book appointments & consultations</p>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </Link>

        {/* Doctor Portal */}
        <Link to="/doctor/login" className="block mb-4">
          <div className="card border-2 border-accent-100 hover:border-accent-300 transition-all hover:shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-accent-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                <span className="text-3xl">👨‍⚕️</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg">Doctor Portal</h3>
                <p className="text-sm text-gray-500">Manage patients & consultations</p>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </Link>

        {/* Admin - hidden but accessible */}
        <div className="text-center mt-8">
          <Link
            to="/admin/login"
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            System Administration
          </Link>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 pb-4">
          <p className="text-xs text-gray-400">
            © 2026 CDCS · Secure Healthcare Platform 🇰🇪
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
